/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect } from 'react';
import Select, { StylesConfig } from 'react-select'
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import { loadCommon } from '../../../actions/common';
import { getCountryOptions } from '../../../selectors/common';
import { BaseStepCompProps } from '../types';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { useUserGeoLocation } from '../../../hooks/useUserGeoLocation';
import { AppFlavour, getDefaultConfig } from '../../../config/app-config';
import variables from '../../../css/variables.module.scss';
import { getCurrencyOptions } from '../../../utils/units';

interface CompanyInfoFormProps extends Pick<BaseStepCompProps, 'updateForm' | 'formData' | 'handleNext' | 'handlePrevious'> {
  handleCreateCompany: () => void;
}

const SINGAPORE_COUNTRY_CODE = 'SG';

export const getStyles = (): StylesConfig<any> => {
  return {
    placeholder: (provided) => ({
      ...provided,
      color: variables.TextPlaceholder,
    }),
  };
};

export const CompanyInfoForm = (props: CompanyInfoFormProps) => {
  const { updateForm, formData, handlePrevious, handleCreateCompany } = props;

  const userLocation = useUserGeoLocation({});
  const dispatch = useAppDispatch();
  const countryList = useAppSelector(getCountryOptions);
  const topCountries = [{ label: 'United Kingdom of Great Britain and Northern Ireland', value: 'GB' }, { value: 'US', label: 'United States of America' }];
  const filteredCountryCodes = topCountries.map(c => c.value);
  const filteredTopCountries = countryList.filter(c => !filteredCountryCodes.includes(c.value));
  const countries = [...topCountries, ...filteredTopCountries];
  const currencyOptions = getCurrencyOptions();

  useEffect(() => {
    if (formData.initiativeCountryCode) {
      return;
    }

    if (userLocation) {
      return updateForm('initiativeCountryCode', userLocation)
    }

    if (getDefaultConfig().flavour === AppFlavour.SINGAPORE) {
      return updateForm('initiativeCountryCode', SINGAPORE_COUNTRY_CODE);
    }

  }, [userLocation, formData.initiativeCountryCode, updateForm])

  const addressData = formData.address;
  const handleAddressInput = (name: string, value: any) => {
    updateForm('address', {
      ...formData['address'],
      [name]: value
    });
  }

  useEffect(() => {
    dispatch(loadCommon());
  }, [dispatch])

  const isFormValid = formData.initiativeCountryCode && formData.initiativeName && formData.unitConfig?.currency;

  const handleNext = (e: React.ChangeEvent<any>) => {
    e.preventDefault();
    if (isFormValid) {
      handleCreateCompany();
    }
  };

  return (
    <section className='form-container'>
      <Form className='mt-5' >
        <div className='row mb-3'>
          <div className='col'>
            <FormGroup>
              <Label for='initiativeName'>What is your company or organisation name?</Label>
              <Input
                id='initiativeName'
                name='initiativeName'
                type='text'
                placeholder='e.g. Alphabet Inc.'
                onChange={(e) => updateForm('initiativeName', e.target.value)}
                value={formData.initiativeName}
                required
              />
            </FormGroup>
          </div>
        </div>
        <div className='row mt-3'>
          <div className='col'>
            <FormGroup className='mb-1'>
              <Label for='line1'>What is your HQ address?</Label>
              <Input
                id='line1'
                name='line1'
                type='text'
                placeholder='First line of address'
                onChange={(e) => handleAddressInput('line1', e.target.value)}
                value={addressData?.line1 ?? ''}
                required
              />
            </FormGroup>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <FormGroup className='mb-1'>
              <Input
                id='line2'
                name='line2'
                type='text'
                placeholder='Second line of address'
                onChange={(e) => handleAddressInput('line2', e.target.value)}
                value={addressData?.line2 ?? ''}
                required
              />
            </FormGroup>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <FormGroup className='mb-1'>
              <Input
                id='city'
                name='city'
                type='text'
                placeholder='Name of town or city'
                onChange={(e) => handleAddressInput('city', e.target.value)}
                value={addressData?.city ?? ''}
                required
              />
            </FormGroup>
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col'>
            <FormGroup>
              <Input
                id='postcode'
                name='postcode'
                type='text'
                placeholder='Postal code / Zip code etc'
                onChange={(e) => handleAddressInput('postcode', e.target.value)}
                value={addressData?.postcode ?? ''}
                required
              />
            </FormGroup>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <FormGroup>
              <Label for='initiativeCountryCode'>Country</Label>
              <Select
                placeholder={'e.g. Singapore'}
                options={countries}
                styles={getStyles()}
                value={countries.find(c => c.value === formData.initiativeCountryCode) ?? null}
                onChange={(option) => updateForm('initiativeCountryCode', option?.value ?? '')}
              />
            </FormGroup>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <FormGroup>
              <Label for='currency'>Reporting currency</Label>
              <Select
                placeholder={'e.g. US Dollars'}
                options={currencyOptions}
                styles={getStyles()}
                value={currencyOptions.find(c => c.value === formData.unitConfig?.currency) ?? null}
                onChange={(option) => updateForm('unitConfig', { ...formData.unitConfig, currency: option?.value ?? '' })}
              />
            </FormGroup>
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col'>
            <FormGroup>
              <Label for='registrationNumber'>Company registration number</Label>
              <Input
                placeholder='Enter unique company number if known'
                id='registrationNumber'
                name='registrationNumber'
                type='text'
                onChange={(e) => updateForm('registrationNumber', e.target.value)}
                value={formData.registrationNumber}
              />
              <FormText>optional</FormText>
            </FormGroup>
          </div>
        </div>
        {/* <div className="row mb-3">
          <div className="col">
            <FormGroup>
              <Label for="financialEndDate">
                What is the end date of your annual financial year?
              </Label>
              <MonthDayPicker value={formData.financialEndDate} onChange={(value) => updateForm('financialEndDate', value)} />
              <FormText>Optional</FormText>
            </FormGroup>
          </div>
        </div> */}
      </Form>
      <div className='mt-5 text-right d-flex'>
        {handlePrevious ? <Button color='link-secondary' onClick={handlePrevious}><i className='far fa-angle-left mr-2' />Back</Button> : null}
        <Button color='primary' disabled={!isFormValid} className='ml-auto' onClick={handleNext}>Next<i className='far fa-angle-right ml-2' /></Button>
      </div>
    </section>
  );
}
